import {
  Banner,
  Box,
  Breadcrumb,
  Grid,
  GridColumn,
  Tabs,
  Text,
  ThemeProvider
} from "@modernatx/ui-kit-react";
import React from "react";
import { useHistory } from "react-router-dom";

import CreditList from "@/components/InvoicePay/CreditList";
import { LoadingSpinner } from "@/components/LoadingSpinner/LoadingSpinner";
import { AutoPayModal } from "@/components/Payments/AutoPayModal/AutoPayModal";
import { SummaryBanner, SummaryBannerProps } from "@/components/SummaryBanner";
import { useFeatureFlag } from "@/context/FeatureFlagContext";
import { useInvoiceService } from "@/hooks/useInvoiceService";
import { useInvoiceSummary } from "@/hooks/useInvoiceSummary";
import { AutoPayTypes } from "@/types";
import { CreditSortableColumnKeys, CreditsViewProps } from "@/types/Credit";
import {
  InvoicesAndPaymentsTabs,
  InvoicesViewProps,
  SortableColumnKeys
} from "@/types/Invoice";
import { attachCurrencySymbolToBalanceString } from "@/utils/InvoicePay";

import InvoiceList from "../components/InvoicePay/InvoiceList";

const pages = [
  { pageId: "your-account", title: "Your Account" },
  {
    pageId: "your-account/invoices-and-payments",
    title: "Invoices and payments"
  }
];

const InvoicesAndPaymentsPage = () => {
  const history = useHistory();
  const creditsEnabled = useFeatureFlag("SEE_AND_APPLY_CREDITS");
  const autoPayAndSchedulePayEnabled = useFeatureFlag(
    "IS_AUTO_PAY_SCHEDULE_PAY_ENABLED"
  );

  const [showAutoPayModal, setShowAutoPayModal] = React.useState(false);

  const { isLoadingSummary, showSummaryError, summaryData } =
    useInvoiceSummary();

  const {
    activeTab,
    onClickTab,
    tabs,
    isLoadingInvoices,
    invoiceError,
    invoiceList,
    pageSize,
    currentPage,
    handlePageChange,
    handlePageSizeChange,
    sortDirection,
    sortProperty,
    handleSort,
    creditList,
    isLoadingCredits,
    creditError
  } = useInvoiceService();

  const invoiceBannerActions: SummaryBannerProps["actions"] = [
    {
      label: "Pay balance",
      onClick: () => {},
      icon: "arrow-right",
      variant: "filled"
    },
    {
      label: "Set up automatic payments",
      onClick: () => {
        setShowAutoPayModal(true);
      },
      variant: "outline"
    }
  ];

  const onClickBreadcrumb = (pageId: string) => {
    history.push(`/${pageId}`);
  };

  const pageContent = (() => {
    switch (activeTab) {
      case InvoicesAndPaymentsTabs.credits.id:
        return (
          <CreditsView
            isLoadingCredits={isLoadingCredits}
            creditError={creditError}
            creditList={creditList}
            pageSize={pageSize}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            handlePageSizeChange={handlePageSizeChange}
            handleSort={handleSort}
            sortDirection={sortDirection}
            sortProperty={sortProperty as CreditSortableColumnKeys}
          />
        );
      case InvoicesAndPaymentsTabs.statements.id:
        return <StatementsView />;
      default:
        return (
          <InvoicesView
            isLoadingInvoices={isLoadingInvoices}
            invoiceError={invoiceError}
            invoiceList={invoiceList}
            pageSize={pageSize}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            handlePageSizeChange={handlePageSizeChange}
            handleSort={handleSort}
            sortDirection={sortDirection}
            sortProperty={sortProperty as SortableColumnKeys}
          />
        );
    }
  })();

  return (
    <ThemeProvider>
      {autoPayAndSchedulePayEnabled && (
        <AutoPayModal
          modalType={AutoPayTypes.AllInvoices}
          isVisible={showAutoPayModal}
          setVisible={setShowAutoPayModal}
        />
      )}

      <Box
        sx={{
          backgroundColor: "surface-neutral-secondary",
          py: "spacing-9",
          userSelect: "text"
        }}
      >
        <Grid>
          <GridColumn sx={{ pb: "spacing-9" }}>
            <Breadcrumb pages={pages} onClickPage={onClickBreadcrumb} />
          </GridColumn>
          <GridColumn sx={{ pt: "spacing-6", pb: "spacing-9" }}>
            <Text variant="h4">Invoices and payments</Text>
          </GridColumn>

          {!isLoadingSummary && (
            <GridColumn sx={{ pb: "spacing-9" }}>
              <SummaryBanner
                label="Outstanding balance"
                amount={attachCurrencySymbolToBalanceString(
                  summaryData?.total ?? 0,
                  summaryData?.currency ?? "USD"
                )}
                {...(creditsEnabled && {
                  onClickCreditsLink: () => onClickTab("credits")
                })}
                credits={attachCurrencySymbolToBalanceString(
                  summaryData?.credits,
                  summaryData?.currency ?? "USD"
                )}
                {...(autoPayAndSchedulePayEnabled && {
                  actions: invoiceBannerActions
                })}
                showError={showSummaryError}
              />
              {activeTab === InvoicesAndPaymentsTabs.credits.id && (
                <Box sx={{ mt: "spacing-9" }}>
                  <Banner
                    variant="info"
                    text="The ability to apply credits to your account is planned for release later this year as part of our continued site enhancements to ModernaDirect.com. To apply any unused credits currently, please contact our Customer Care Team at 1-866-MODERNA (1-866-663-3762)."
                  />
                </Box>
              )}
              {creditsEnabled && (
                <Box sx={{ mt: "spacing-9" }}>
                  <Tabs
                    key={activeTab}
                    items={tabs}
                    defaultActive={activeTab}
                    onClickTab={onClickTab}
                  />
                </Box>
              )}
            </GridColumn>
          )}
        </Grid>
        {pageContent}
      </Box>
    </ThemeProvider>
  );
};

const InvoicesView: React.FC<InvoicesViewProps> = (props) => {
  const {
    isLoadingInvoices,
    invoiceError,
    invoiceList,
    pageSize,
    currentPage,
    handlePageChange,
    handlePageSizeChange,
    sortDirection,
    sortProperty,
    handleSort
  } = props;

  return (
    <Grid>
      <GridColumn sx={{ pb: "spacing-10" }}>
        {isLoadingInvoices ? (
          <LoadingSpinner size="xl" />
        ) : invoiceError ? (
          <Text sx={{ pt: "spacing-6", color: "color-system-red-700" }}>
            {invoiceError}
          </Text>
        ) : !invoiceList.invoices.length ? (
          <Text sx={{ pt: "spacing-6" }} color="text-error">
            No invoices found.
          </Text>
        ) : (
          <InvoiceList
            invoiceList={invoiceList.invoices || []}
            pageSize={pageSize}
            totalPage={Math.ceil(
              (invoiceList?.pagination?.total ?? 0) / pageSize
            )}
            totalInvoices={invoiceList.pagination.total}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            sortDirection={sortDirection}
            sortProperty={sortProperty}
            onSort={handleSort}
          />
        )}
      </GridColumn>
    </Grid>
  );
};

const CreditsView: React.FC<CreditsViewProps> = (props) => {
  const {
    isLoadingCredits,
    creditError,
    creditList,
    pageSize,
    currentPage,
    handlePageChange,
    handlePageSizeChange,
    sortDirection,
    sortProperty,
    handleSort
  } = props;

  return (
    <Grid>
      <GridColumn>
        {isLoadingCredits ? (
          <LoadingSpinner size="xl" />
        ) : creditError ? (
          <Text sx={{ pt: "spacing-6", color: "color-system-red-700" }}>
            {creditError}
          </Text>
        ) : creditList.credits.length === 0 ? (
          <Text sx={{ pt: "spacing-6" }} color="text-error">
            No credits found.
          </Text>
        ) : (
          <CreditList
            creditList={creditList.credits}
            pageSize={pageSize}
            currentPage={currentPage}
            totalPage={Math.ceil((creditList.pagination.total ?? 0) / pageSize)}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            totalCredits={creditList.pagination.total}
            sortDirection={sortDirection}
            sortProperty={sortProperty}
            onSort={handleSort}
          />
        )}
      </GridColumn>
    </Grid>
  );
};

const StatementsView: React.FC = () => {
  return (
    <Grid>
      <GridColumn>
        <Text>Statements Content</Text>
      </GridColumn>
    </Grid>
  );
};

export default InvoicesAndPaymentsPage;
